/*
 * TBI Fusion Pay — informational variant.
 *
 * Phase 2 redesign (matches matraci.bg): the storefront button is purely
 * informational. It calls GetCalculations to display monthly installment
 * options, but never calls RegisterApplication and never redirects the
 * customer. The actual credit application is initiated by the manual
 * payment method "Стоков кредит (TBI)" at Shopify checkout.
 */
(() => {
  const els = {
    modal: null,
    loading: null,
    error: null,
    content: null,
    periodSelect: null,
    row: null,
    cellInstallment: null,
    cellTotal: null,
    cellApr: null,
    cellNir: null,
    title: null,
  };

  const state = {
    eurBgnRate: 1.95583,
    showBgnEquivalent: true,
    activeRoot: null,
    schemesByPeriod: new Map(),
    currentPeriod: null,
    currentAmountEur: 0,
  };

  const $ = (sel, root = document) => root.querySelector(sel);
  const $all = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  function looksLikeDirectTbiApiBase(url) {
    const v = String(url || '').toLowerCase();
    return v.includes('tbibank.support') || v.includes('/api/getcalculations') || v.includes('/api/registerapplication');
  }

  function buildApiUrl(base, path) {
    const b = String(base || '').replace(/\/+$/, '');
    const p = String(path || '').replace(/^\/+/, '');
    return `${b}/${p}`;
  }

  function fmtEur(amount) {
    try {
      return new Intl.NumberFormat('bg-BG', { style: 'currency', currency: 'EUR' }).format(amount);
    } catch {
      return `${amount.toFixed(2)} €`;
    }
  }

  function fmtBgn(amount) {
    try {
      return new Intl.NumberFormat('bg-BG', { style: 'currency', currency: 'BGN' }).format(amount);
    } catch {
      return `${amount.toFixed(2)} лв.`;
    }
  }

  function fmtPct(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return '—';
    return `${n.toFixed(2)}%`;
  }

  async function fetchJson(url, opts) {
    let res;
    try {
      res = await fetch(url, opts);
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      err.url = url;
      err.isNetwork = true;
      throw err;
    }
    const text = await res.text();
    let json;
    try {
      json = text ? JSON.parse(text) : null;
    } catch {
      json = null;
    }
    if (!res.ok) {
      const msg =
        json?.error?.message ||
        json?.message ||
        (typeof json?.error === 'string' ? json.error : '') ||
        text ||
        `HTTP ${res.status}`;
      const err = new Error(msg);
      err.status = res.status;
      err.url = url;
      err.raw = json;
      throw err;
    }
    return json;
  }

  async function getCart() {
    return fetchJson('/cart.js', { method: 'GET' });
  }

  async function resolveAmountEur(root) {
    const ctx = root.getAttribute('data-tbi-context') || 'cart';
    if (ctx === 'product') {
      const cents = Number(root.getAttribute('data-tbi-amount-cents') || '0');
      return cents > 0 ? cents / 100 : 0;
    }
    const cart = await getCart();
    const cents = Number(cart?.total_price || 0);
    return cents > 0 ? cents / 100 : 0;
  }

  function bindModal() {
    els.modal = $('#TbiFusionPayModal');
    if (!els.modal) return;
    els.loading = $('[data-tbi-modal-loading]', els.modal);
    els.error = $('[data-tbi-modal-error]', els.modal);
    els.content = $('[data-tbi-modal-content]', els.modal);
    els.periodSelect = $('[data-tbi-modal-period-select]', els.modal);
    els.row = $('[data-tbi-modal-row]', els.modal);
    els.cellInstallment = $('[data-tbi-cell-installment]', els.modal);
    els.cellTotal = $('[data-tbi-cell-total]', els.modal);
    els.cellApr = $('[data-tbi-cell-apr]', els.modal);
    els.cellNir = $('[data-tbi-cell-nir]', els.modal);
    els.title = $('#TbiFusionPayModalTitle', els.modal);

    $all('[data-tbi-modal-close]', els.modal).forEach((el) => {
      el.addEventListener('click', (ev) => {
        ev.preventDefault();
        closeModal();
      });
    });

    document.addEventListener('keydown', (ev) => {
      if (ev.key === 'Escape' && els.modal && !els.modal.hidden) closeModal();
    });
  }

  function openModal() {
    if (!els.modal) return;
    els.modal.hidden = false;
    document.documentElement.classList.add('tbi-fusion-pay-modal-open');
    setModalState('loading');
    const closeBtn = $('[data-tbi-modal-close]', els.modal);
    if (closeBtn) closeBtn.focus();
  }

  function closeModal() {
    if (!els.modal) return;
    els.modal.hidden = true;
    document.documentElement.classList.remove('tbi-fusion-pay-modal-open');
  }

  function setModalState(next) {
    if (!els.loading || !els.error || !els.content) return;
    els.loading.hidden = next !== 'loading';
    els.error.hidden = next !== 'error';
    els.content.hidden = next !== 'content';
  }

  function showError(message) {
    setModalState('error');
    if (!els.error) return;
    els.error.textContent = message || 'Възникна грешка при зареждане на схемите за разсрочване.';
  }

  /**
   * Pick a representative scheme per period (lowest monthly installment if multiple).
   * Returns Map<period:number, scheme>.
   */
  function groupSchemesByPeriod(schemes, amountEur) {
    const byPeriod = new Map();
    for (const s of schemes) {
      const period = Number(s.period);
      if (!Number.isFinite(period) || period <= 0) continue;
      const monthly = amountEur * Number(s.installment_factor);
      const prev = byPeriod.get(period);
      if (!prev) {
        byPeriod.set(period, s);
        continue;
      }
      const prevMonthly = amountEur * Number(prev.installment_factor);
      if (monthly < prevMonthly) byPeriod.set(period, s);
    }
    return new Map([...byPeriod.entries()].sort((a, b) => a[0] - b[0]));
  }

  function renderPeriodOptions() {
    if (!els.periodSelect) return;
    els.periodSelect.innerHTML = '';
    const amountEur = state.currentAmountEur;
    for (const [period, scheme] of state.schemesByPeriod) {
      const monthlyEur = amountEur * Number(scheme.installment_factor || 0);
      const opt = document.createElement('option');
      opt.value = String(period);
      const label = `${period} ${period === 1 ? 'месец' : 'месеца'}`;
      const monthly = monthlyEur > 0 ? ` — ${fmtEur(monthlyEur)}/месец` : '';
      opt.textContent = `${label}${monthly}`;
      els.periodSelect.appendChild(opt);
    }
    if (!els.periodSelect.dataset.bound) {
      els.periodSelect.addEventListener('change', (ev) => {
        const val = Number(ev.target.value);
        if (Number.isFinite(val)) selectPeriod(val);
      });
      els.periodSelect.dataset.bound = '1';
    }
  }

  function selectPeriod(period) {
    state.currentPeriod = period;
    const scheme = state.schemesByPeriod.get(period);
    if (!scheme) return;

    if (els.periodSelect && Number(els.periodSelect.value) !== period) {
      els.periodSelect.value = String(period);
    }

    const amountEur = state.currentAmountEur;
    const monthlyEur = amountEur * Number(scheme.installment_factor || 0);
    const totalEur = amountEur * Number(scheme.total_due_factor || 0);

    if (els.cellInstallment) {
      if (state.showBgnEquivalent) {
        const monthlyBgn = monthlyEur * state.eurBgnRate;
        els.cellInstallment.innerHTML = `${fmtEur(monthlyEur)} <span class="tbi-fusion-pay-modal__bgn">${fmtBgn(monthlyBgn)}</span>`;
      } else {
        els.cellInstallment.textContent = fmtEur(monthlyEur);
      }
    }
    if (els.cellTotal) {
      els.cellTotal.textContent = totalEur > 0 ? fmtEur(totalEur) : '—';
    }
    if (els.cellApr) {
      els.cellApr.textContent = fmtPct(scheme.apr);
    }
    if (els.cellNir) {
      els.cellNir.textContent = fmtPct(scheme.nir);
    }
  }

  function pickInitialPeriod() {
    const periods = [...state.schemesByPeriod.keys()];
    if (periods.length === 0) return null;
    // Prefer 12 months if present (most relatable default), else lowest period.
    return periods.includes(12) ? 12 : periods[0];
  }

  async function loadAndRender(root) {
    const apiBase = (root.getAttribute('data-tbi-api-base') || '').trim();
    if (!apiBase || looksLikeDirectTbiApiBase(apiBase)) {
      showError('Backend URL не е настроен правилно.');
      return;
    }

    state.activeRoot = root;
    state.eurBgnRate = Number(root.getAttribute('data-tbi-eur-bgn-rate') || '1.95583') || 1.95583;

    const amountEur = await resolveAmountEur(root);
    if (!Number.isFinite(amountEur) || amountEur <= 0) {
      showError('Невалидна сума. Моля, обновете страницата и опитайте отново.');
      return;
    }
    state.currentAmountEur = amountEur;

    const url = buildApiUrl(apiBase, `calculations?amount=${encodeURIComponent(amountEur)}&currency=EUR`);

    let data;
    try {
      data = await fetchJson(url, { method: 'GET' });
    } catch (e) {
      showError(e?.message || 'TBI backend недостъпен.');
      return;
    }

    if (!data?.ok) {
      showError(data?.error?.message || 'Невалиден отговор от TBI.');
      return;
    }
    const schemes = Array.isArray(data.schemes) ? data.schemes : [];
    if (schemes.length === 0) {
      showError('Няма налични схеми за разсрочване за тази сума.');
      return;
    }

    state.schemesByPeriod = groupSchemesByPeriod(schemes, amountEur);
    if (state.schemesByPeriod.size === 0) {
      showError('Няма валидни схеми за тази сума.');
      return;
    }

    renderPeriodOptions();
    const initial = pickInitialPeriod();
    if (initial != null) selectPeriod(initial);

    setModalState('content');
  }

  /**
   * Per-instance eligibility check: hide the pill button when the
   * applicable amount is below the configured EUR threshold.
   * For 'product' context we use the variant price snapshot from Liquid.
   * For 'cart' context we fetch /cart.js (so the pill stays accurate after AJAX updates).
   */
  async function refreshEligibility(root) {
    const ctx = root.getAttribute('data-tbi-context') || 'cart';
    const minEur = Number(root.getAttribute('data-tbi-min-eur') || '50') || 50;

    let amountEur = 0;
    if (ctx === 'product') {
      const cents = Number(root.getAttribute('data-tbi-amount-cents') || '0');
      amountEur = cents > 0 ? cents / 100 : 0;
    } else {
      try {
        const cart = await getCart();
        const cents = Number(cart?.total_price || 0);
        amountEur = cents > 0 ? cents / 100 : 0;
      } catch {
        amountEur = 0;
      }
    }

    if (ctx === 'product') {
      // Product pages always show the min-amount note; the pill only when eligible.
      root.hidden = false;
      const pill = $('[data-tbi-fusion-pay-button]', root);
      if (pill) pill.hidden = amountEur < minEur;
      return;
    }

    if (amountEur >= minEur) {
      root.hidden = false;
    } else {
      root.hidden = true;
    }
  }

  function initInstance(root) {
    if (root.dataset.tbiBound === '1') return;
    root.dataset.tbiBound = '1';

    refreshEligibility(root);

    const btn = $('[data-tbi-fusion-pay-button]', root);
    if (!btn) return;

    btn.addEventListener('click', async (ev) => {
      ev.preventDefault();
      if (!els.modal) return;
      openModal();
      await loadAndRender(root);
    });
  }

  function init() {
    bindModal();
    const roots = $all('[data-tbi-fusion-pay]');
    if (!roots.length || !els.modal) return;
    roots.forEach(initInstance);

    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (!(node instanceof Element)) continue;
          const target = node.matches?.('[data-tbi-fusion-pay]') ? node : node.querySelector?.('[data-tbi-fusion-pay]');
          if (target) initInstance(target);
        }
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });

    document.addEventListener('cart:updated', () => {
      $all('[data-tbi-fusion-pay][data-tbi-context="cart"]').forEach(refreshEligibility);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
